/**
 * Test Drive — Public Booking Landing Page (iter133b — Phase 2)
 *
 * Reached from the embed's Test Drive popup (`/test-drive/book?pk=<publicKey>`).
 * Public — NO auth. Renders:
 *   1. Dealer name + denomination (fetched from /public/test-drive/campaign)
 *   2. Name / email / phone / preferred date / preferred time
 *   3. Required, unchecked-by-default consent checkbox with the exact
 *      disclosure text stored in the backend (single source of truth)
 *   4. OTP entry (after submit)
 *
 * On successful verify: the customer receives an SMS with their claim
 * ticket. NO spendable code is shown or texted here.
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtCents(cents) {
  if (typeof cents !== "number") return "";
  return `$${(cents / 100).toFixed(0)}`;
}

export default function TestDriveBookPage() {
  const publicKey = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("pk") || p.get("public_key") || "";
  }, []);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("form"); // form | otp | done
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "10:00",
    consent: false,
  });
  const [bookingId, setBookingId] = useState(null);
  const [otp, setOtp] = useState("");
  const [doneMessage, setDoneMessage] = useState("");

  useEffect(() => {
    async function load() {
      if (!publicKey) { setError("Missing public key."); setLoading(false); return; }
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/test-drive/campaign`, { params: { public_key: publicKey } });
        setCampaign(res.data);
        if (!res.data?.active) setError("Test Drive isn't currently active for this dealer.");
      } catch (e) {
        setError("Failed to load the campaign.");
      } finally { setLoading(false); }
    }
    load();
  }, [publicKey]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { setError("Please check the consent box to continue."); return; }
    setSubmitting(true); setError(null);
    try {
      const preferredIso = new Date(`${form.date}T${form.time}:00`).toISOString();
      const res = await axios.post(`${BACKEND_URL}/api/public/test-drive/book`, {
        public_key: publicKey,
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim(),
        preferred_datetime: preferredIso,
        consent_checked: true,
        consent_version: campaign?.consent_version || "draft_v1",
      });
      setBookingId(res.data.booking_id);
      if (res.data.otp_sent) {
        setStep("otp");
      } else {
        setError(`Booked but SMS failed to send: ${res.data.otp_error}. Contact the dealer directly.`);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Booking failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/test-drive/verify`, {
        booking_id: bookingId,
        otp_code: otp.trim(),
      });
      if (res.data.success) {
        setDoneMessage(res.data.message);
        setStep("done");
      } else {
        setError(res.data.message || "Invalid code, try again.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Verification failed.");
    } finally { setSubmitting(false); }
  };

  if (loading) return <PageShell><p className="text-gray-500">Loading…</p></PageShell>;
  if (!campaign?.active) return <PageShell><p className="text-gray-700" data-testid="td-book-inactive">{error || "This booking page isn't live yet."}</p></PageShell>;

  const denom = fmtCents(campaign.denomination_cents);

  if (step === "done") {
    return (
      <PageShell>
        <div data-testid="td-book-done" className="space-y-3">
          <div className="text-3xl">✅</div>
          <h1 className="text-2xl font-bold text-gray-900">You&apos;re booked!</h1>
          <p className="text-gray-600">{doneMessage}</p>
          <p className="text-sm text-gray-500">
            Bring the claim ticket we just texted you to <strong>{campaign.dealer_name}</strong>.
            Staff will send your {denom} gift card to your phone when you arrive.
          </p>
        </div>
      </PageShell>
    );
  }

  if (step === "otp") {
    return (
      <PageShell>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify your phone</h1>
        <p className="text-sm text-gray-600 mb-4">We texted a 6-digit code to <strong>{form.phone}</strong>. Enter it below.</p>
        <form onSubmit={verify} className="space-y-4" data-testid="td-book-otp-form">
          <input
            type="text" inputMode="numeric" maxLength={6}
            className="w-full text-center text-2xl font-mono tracking-widest border-2 border-gray-300 rounded-lg px-3 py-3 focus:outline-none focus:border-red-500"
            value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            data-testid="td-book-otp" required
            placeholder="000000"
          />
          {error && <p className="text-red-600 text-sm" data-testid="td-book-otp-error">{error}</p>}
          <button
            type="submit" disabled={submitting || otp.length < 4}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
            data-testid="td-book-verify-btn"
          >
            {submitting ? "Verifying…" : "Verify & get my claim ticket"}
          </button>
        </form>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="td-book-headline">Book a test drive, get a {denom} gift card</h1>
        <p className="text-sm text-gray-600 mt-1">at <strong>{campaign.dealer_name}</strong></p>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="td-book-form">
        <Field label="Full name">
          <input type="text" required className="input" data-testid="td-book-name" value={form.name} onChange={(e) => setForm(s => ({...s, name: e.target.value}))} />
        </Field>
        <Field label="Email (optional)">
          <input type="email" className="input" data-testid="td-book-email" value={form.email} onChange={(e) => setForm(s => ({...s, email: e.target.value}))} />
        </Field>
        <Field label="Mobile phone">
          <input type="tel" required className="input" data-testid="td-book-phone" placeholder="+1…" value={form.phone} onChange={(e) => setForm(s => ({...s, phone: e.target.value}))} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Preferred date">
            <input type="date" required className="input" data-testid="td-book-date"
              min={new Date().toISOString().slice(0,10)}
              max={new Date(Date.now() + 14*24*3600*1000).toISOString().slice(0,10)}
              value={form.date} onChange={(e) => setForm(s => ({...s, date: e.target.value}))} />
          </Field>
          <Field label="Preferred time">
            <select className="input" data-testid="td-book-time" value={form.time} onChange={(e) => setForm(s => ({...s, time: e.target.value}))}>
              {Array.from({length: 10}, (_, i) => 9 + i).map(h => (
                <option key={h} value={`${String(h).padStart(2,"0")}:00`}>{h > 12 ? `${h-12}:00 PM` : (h === 12 ? "12:00 PM" : `${h}:00 AM`)}</option>
              ))}
            </select>
          </Field>
        </div>
        <label className="flex items-start gap-2 text-xs text-gray-600 leading-relaxed" data-testid="td-book-consent-label">
          <input type="checkbox" checked={form.consent} onChange={(e) => setForm(s => ({...s, consent: e.target.checked}))} data-testid="td-book-consent" className="mt-1" />
          <span>{campaign.consent_disclosure}</span>
        </label>
        {error && <p className="text-red-600 text-sm" data-testid="td-book-error">{error}</p>}
        <button type="submit" disabled={submitting || !form.consent}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
          data-testid="td-book-submit">
          {submitting ? "Submitting…" : "Book my test drive"}
        </button>
      </form>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6">
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
